import {API_URL, GetBrowserData} from '../../../../_metronic/helpers'
import {AuthModel} from './_models'
import {CORRELATION_HEADER, createCorrelationId, notifyCorrelationId} from '../../../observability'
import {LOGIN_SESSION_STORAGE_PREFIX} from './loginSessionStorage'
import {ACCOUNT_INCOMPLETE, ACCOUNT_INCOMPLETE_EVENT, DEVICE_LIMIT} from './accountGateCodes'
import {
  BUSINESS_NOT_AVAILABLE,
  announceBusinessSwitch,
  leaveUnavailableBusiness,
  rememberTabBusiness,
  tabBusinessHeaders,
  tabBusinessId,
} from './tabBusiness'

const AUTH_LOCAL_STORAGE_KEY = 'authInfo'
const SUPPORT_SESSION_STORAGE_KEY = 'megapay.support.session'
const API_CONNECTION_ONLINE_EVENT = 'megapay:api:online'
const API_CONNECTION_OFFLINE_EVENT = 'megapay:api:offline'

// A refusal for the account (ACCOUNT_INCOMPLETE, DEVICE_LIMIT) was announced and the session check it asks for is under
// way or done; the next one is announced only once the session's user changes (Auth.tsx).
let accountGateAnnounced = false

/** The session's user changed (its account completed, a device signed out): a later refusal is announced again. */
export const resetAccountGateNotice = () => {
  accountGateAnnounced = false
}

const isConnectionError = (error: any) => {
  return (
    !error?.response &&
    (error?.code === 'ERR_NETWORK' ||
      error?.code === 'ECONNABORTED' ||
      error?.message === 'Network Error')
  )
}

const isApiRequest = (config: any) => {
  if (!config?.url) return false

  try {
    const apiBase = new URL(API_URL, window.location.origin)
    const requestUrl = new URL(config.url, config.baseURL || window.location.origin)
    const apiPath = apiBase.pathname.replace(/\/$/, '')
    return (
      requestUrl.origin === apiBase.origin &&
      (apiPath === '' ||
        requestUrl.pathname === apiPath ||
        requestUrl.pathname.startsWith(`${apiPath}/`))
    )
  } catch {
    return false
  }
}

const isChangeCompany = (config: any) => {
  if (!config?.url || !isApiRequest(config)) return false
  try {
    const url = new URL(config.url, config.baseURL || window.location.origin)
    return /\/auth\/changeCompany\/?$/i.test(url.pathname)
  } catch {
    return false
  }
}

const getAuth = (): AuthModel | undefined => {
  if (typeof sessionStorage === 'undefined') return undefined
  try {
    const value = sessionStorage.getItem(SUPPORT_SESSION_STORAGE_KEY)
    const auth = value ? (JSON.parse(value) as AuthModel) : undefined
    if (
      auth?.tk &&
      auth.support?.session_type === 'support' &&
      auth.support?.support_mode === 'readonly' &&
      auth.support.expires_at * 1000 > Date.now()
    ) {
      return auth
    }
    sessionStorage.removeItem(SUPPORT_SESSION_STORAGE_KEY)
  } catch {
    sessionStorage.removeItem(SUPPORT_SESSION_STORAGE_KEY)
  }
  return undefined
}

const setAuth = (auth: AuthModel) => {
  if (typeof sessionStorage === 'undefined') return
  if (auth?.tk && auth.support?.session_type === 'support') {
    sessionStorage.setItem(SUPPORT_SESSION_STORAGE_KEY, JSON.stringify(auth))
  }
}

const removeSupportAuth = () => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(SUPPORT_SESSION_STORAGE_KEY)
  } catch (error) {
    console.error('SUPPORT SESSION STORAGE REMOVE ERROR', error)
  }
}

const clearLoginSessionStorage = () => {
  if (typeof sessionStorage === 'undefined') return
  try {
    const keys: string[] = []
    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index)
      if (key && key.startsWith(LOGIN_SESSION_STORAGE_PREFIX)) keys.push(key)
    }
    keys.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // Without sessionStorage nothing was kept for the sign-in.
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
    removeSupportAuth()
    clearLoginSessionStorage()
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.defaults.withCredentials = true
  axios.interceptors.request.use(
    async (config: any) => {
      config.headers = config.headers || {}
      if (isApiRequest(config)) {
        config.headers[CORRELATION_HEADER] =
          config.headers[CORRELATION_HEADER] || createCorrelationId()
        const supportAuth = getAuth()
        if (supportAuth?.tk && !String(config.url || '').includes('/support-sessions/exchange')) {
          config.headers.Authorization = `Bearer ${supportAuth.tk}`
        }
        // The tab's own business (tabBusiness): only once the server said it reads the header.
        Object.assign(config.headers, tabBusinessHeaders())
      }
      const browser = await GetBrowserData()
      config.headers.Browser = JSON.stringify(browser)
      return config
    },
    (err: any) => Promise.reject(err)
  )
  axios.interceptors.response.use(
    (response: any) => {
      notifyCorrelationId(response?.headers?.['x-correlation-id'])
      window.dispatchEvent(new Event(API_CONNECTION_ONLINE_EVENT))
      // Every picker chooses a business through auth/changeCompany: the tab keeps the business it chose, and the
      // other tabs of the browser hear of it (they keep their own).
      if (isChangeCompany(response?.config) && typeof response?.data?.cpi === 'number') {
        const from = tabBusinessId()
        rememberTabBusiness(response.data)
        const to = response.data.cpi as number
        if (to !== from) {
          announceBusinessSwitch({
            from,
            to,
            userId: response.data.id,
            name: (response.data.cpl || []).find((company: {id: number}) => company.id === to)?.nm,
          })
        }
      }
      return response
    },
    (error: any) => {
      notifyCorrelationId(
        error?.response?.headers?.['x-correlation-id'] ||
          error?.config?.headers?.[CORRELATION_HEADER]
      )
      if (error?.response?.status === 401) {
        const supportSession = Boolean(getAuth())
        if (supportSession) removeSupportAuth()
        else removeAuth()
        window.dispatchEvent(
          new CustomEvent('megapay:auth:unauthorized', {
            detail: {supportSession},
          })
        )
      }
      if (
        error?.response?.status === 403 &&
        error?.response?.data?.code === 'SUPPORT_MODE_READ_ONLY'
      ) {
        window.dispatchEvent(
          new CustomEvent('megapay:support:readonly-blocked', {
            detail: error.response.data,
          })
        )
      }
      // «محدودیت ورود»: the business is closed to the user now; AccessRestrictedNotice says why (backend 1.0.0.642+).
      if (
        (error?.response?.status === 403 || error?.response?.status === 423) &&
        error?.response?.data?.code === 'ACCESS_RESTRICTED'
      ) {
        window.dispatchEvent(new CustomEvent('megapay:access:restricted', {detail: error.response.data}))
      }
      // The account lacks its mobile, name or own username and password, or this sign-in is past the two devices of
      // its kind (backend 1.0.0.757+): not a sign-out — the session is checked again and the panel shows only
      // «تکمیل حساب کاربری» or «انتخاب دستگاه». Once per state of the session's user (resetAccountGateNotice), so a
      // component that polls cannot keep the session check going.
      if (
        error?.response?.status === 403 &&
        (error?.response?.data?.code === ACCOUNT_INCOMPLETE ||
          error?.response?.data?.code === DEVICE_LIMIT) &&
        !accountGateAnnounced
      ) {
        accountGateAnnounced = true
        window.dispatchEvent(new Event(ACCOUNT_INCOMPLETE_EVENT))
      }
      // The tab's business is no longer the user's (taken off it, or switched off): the tab opens the session's.
      if (error?.response?.status === 403 && error?.response?.data?.code === BUSINESS_NOT_AVAILABLE) {
        leaveUnavailableBusiness(error.response.data?.message)
      }
      if (isConnectionError(error)) {
        window.dispatchEvent(new Event(API_CONNECTION_OFFLINE_EVENT))
      } else {
        window.dispatchEvent(new Event(API_CONNECTION_ONLINE_EVENT))
      }
      return Promise.reject(error)
    }
  )
}

export {
  getAuth,
  setAuth,
  removeAuth,
  removeSupportAuth,
  clearLoginSessionStorage,
  LOGIN_SESSION_STORAGE_PREFIX,
  AUTH_LOCAL_STORAGE_KEY,
  API_CONNECTION_ONLINE_EVENT,
  API_CONNECTION_OFFLINE_EVENT,
  SUPPORT_SESSION_STORAGE_KEY,
}
